import { channel } from 'redux-saga';
import { v4 } from 'uuid';
import createSagaMiddleware from '@redux-saga/core';
import { createSlice, configureStore } from '@reduxjs/toolkit';
import { processPayloadTemplate, evaluateChoiceRule } from '@code2io/common-flow-utilities';
import { delay, call, takeEvery, spawn, fork, take } from 'redux-saga/effects';
import jp from 'jsonpath';
import { isPlainObject } from 'lodash';

const FlowControlActionType = {
  actionCompleted: "FLOW_ACTION_COMPLETED",
  triggerFlow: "TRIGGER_FLOW",
  triggerAtState: "TRIGGER_AT_STATE",
  flowCompleted: "FLOW_COMPLETED",
  next: "FLOW_EXECUTE_NEXT_ACTION",
  executionError: "FLOW_EXECUTION_ERROR"
};
var ResourceTypes = /* @__PURE__ */ ((ResourceTypes2) => {
  ResourceTypes2["executeBackendFlow"] = "executeBackendFlow";
  return ResourceTypes2;
})(ResourceTypes || {});

const data = {};
function init(executionId, input, context, channel2) {
  data[executionId] = {
    input,
    context,
    channel: channel2
  };
}
function finalize(executionId) {
  var _a;
  (_a = data[executionId].channel) == null ? void 0 : _a.close();
  delete data[executionId];
}
function setInputObject(executionId, inputObj) {
  data[executionId].input = inputObj;
}
function getInputObject(executionId) {
  return data[executionId].input;
}
function getChannel(executionId) {
  return data[executionId].channel;
}
function getContext(executionId) {
  return data[executionId].context;
}
function setContext(executionId, context) {
  data[executionId].context = context;
}
function flowInitializer(input, context) {
  const executionId = v4();
  init(executionId, input != null ? input : {}, context != null ? context : {}, channel());
  return executionId;
}

let flowMetadata = {
  flowManifest: {},
  mode: "prod",
  getAppVariables: () => ({}),
  getPageVariables: () => ({}),
  onNavigate: () => {
  },
  dispatch: () => {
  },
  getAppState: () => {
  }
};
const getFlowMetadata = () => {
  return flowMetadata;
};
const setFlowMetadata = (settings) => {
  flowMetadata = settings;
};
const getManifest = function(flowId) {
  return getFlowMetadata().flowManifest[flowId];
};
const getState = function(flowId, stateId) {
  return findStateRecursive(getFlowMetadata().flowManifest[flowId], stateId);
};
function findStateRecursive(machine, stateId) {
  if (machine.States[stateId]) {
    return machine.States[stateId];
  } else {
    let result;
    Object.values(machine.States).filter(
      (state) => state.Type === "Map"
    ).forEach((state) => {
      if (state.Type === "Map") {
        if (!result) {
          result = findStateRecursive(state.Iterator, stateId);
        }
      }
    });
    return result;
  }
}

const MAX_ITERATIONS = 1e3;
function map(spec, subFlowTrigger) {
  const run = function* (input, context, identifier) {
    var _a, _b;
    const processedParameters = processPayloadTemplate(spec.Parameters, input);
    const { Iterator } = spec;
    let iterations = 0;
    if (processedParameters.forever) {
      let iterationResult2 = null;
      let iterationInput2 = input;
      let iterationContext2 = context;
      while (true) {
        iterations++;
        if (iterations > MAX_ITERATIONS) {
          return {
            success: false,
            errorMsg: "Exceed maximum number of iterations"
          };
        }
        yield delay(0);
        const subFlowResult = yield call(
          subFlowTrigger,
          Iterator,
          iterationInput2,
          iterationContext2,
          identifier.flowId
        );
        if (subFlowResult.success) {
          [iterationResult2, iterationInput2, iterationContext2] = [
            subFlowResult.output,
            subFlowResult.inputObject,
            subFlowResult.contextObject
          ];
          if ((_a = iterationResult2 == null ? void 0 : iterationResult2.vars) == null ? void 0 : _a.break) {
            break;
          }
        }
        if (!subFlowResult.success && subFlowResult.errorMsg) {
          return {
            success: false,
            errorMsg: subFlowResult.errorMsg
          };
        }
      }
      return {
        success: true,
        output: iterationResult2,
        next: spec.Next
      };
    }
    const { array, variableName } = processedParameters;
    let iterationResult = null;
    let iterationInput = input;
    let iterationContext = context;
    for (let i = 0; i < array.length; i++) {
      iterations++;
      if (iterations > MAX_ITERATIONS) {
        return {
          success: false,
          errorMsg: "Exceed maximum number of iterations"
        };
      }
      iterationInput[variableName] = array[i];
      yield delay(0);
      const subFlowResult = yield call(
        subFlowTrigger,
        Iterator,
        iterationInput,
        iterationContext,
        identifier.flowId
      );
      if (subFlowResult.success) {
        [iterationResult, iterationInput, iterationContext] = [
          subFlowResult.output,
          subFlowResult.inputObject,
          subFlowResult.contextObject
        ];
        if ((_b = iterationResult == null ? void 0 : iterationResult.vars) == null ? void 0 : _b.break) {
          break;
        }
      }
      if (!subFlowResult.success && subFlowResult.errorMsg) {
        return {
          success: false,
          errorMsg: subFlowResult.errorMsg
        };
      }
    }
    return {
      success: true,
      output: iterationResult,
      next: spec.Next
    };
  };
  return {
    run
  };
}

function choice(spec) {
  const run = function* (input, context) {
    try {
      const result = evaluateChoiceRule({ Choices: spec.Choices, Default: spec.Default }, input);
      return {
        success: true,
        output: input,
        next: result.Next
      };
    } catch (err) {
      return {
        success: false,
        errorMsg: err.message
      };
    }
  };
  return {
    run
  };
}

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
function request(url, requestOptions) {
  return __async(this, null, function* () {
    try {
      const response = yield fetch(url, requestOptions);
      const result = yield response.json();
      return {
        success: true,
        result
      };
    } catch (error) {
      return {
        success: false,
        error
      };
    }
  });
}

function* makeHttpRequest(payload) {
  if (payload.searchParams) {
    const urlObj = new URL(payload.url);
    urlObj.search = new URLSearchParams(payload.searchParams).toString();
    payload.url = urlObj.toString();
  }
  const response = yield call(request, payload.url, payload.requestOptions);
  return {
    success: response.success,
    [response.success ? "output" : "errorMsg"]: response.success ? response.result : response.error.message
  };
}
function* log(payload) {
  console.log(payload.input);
  return {
    success: true,
    output: {}
  };
}
function* showNotification(payload) {
  const dispatch = getFlowMetadata().dispatch;
  dispatch({
    type: "globalComponent/showToast",
    payload
  });
  return {
    success: true,
    output: {}
  };
}
function* transformation(payload) {
  return {
    success: true,
    output: payload.expression
  };
}
function* dispatchAction(payload) {
  const dispatch = getFlowMetadata().dispatch;
  dispatch({
    type: payload.actionType,
    payload: payload.actionPayload
  });
  return {
    success: true
  };
}
function* selectStateValue(payload) {
  const state = getFlowMetadata().getAppState();
  const val = jp.value(state, payload.path);
  return {
    success: true,
    output: val
  };
}
function* sleep(payload) {
  yield delay(payload.ms);
  return {
    success: true,
    output: {}
  };
}
function* executeFunction(payload) {
  const func = new Function("value", payload.code);
  return {
    success: true,
    output: func(payload.value)
  };
}
function* triggerSubflow(payload, subFlowTrigger) {
  const flowResult = yield call(subFlowTrigger, payload.flowId, payload.input || {}, payload.context || {});
  if (flowResult.success) {
    return {
      success: true,
      output: flowResult.output
    };
  }
  return {
    success: false,
    errorMsg: flowResult.errorMsg
  };
}
function* navigate(payload) {
  getFlowMetadata().onNavigate(payload.path);
  return {
    success: true,
    output: {}
  };
}
const taskExecutors = {
  "http": makeHttpRequest,
  "log": log,
  "sleep": sleep,
  "dispatchAction": dispatchAction,
  "selectStateValue": selectStateValue,
  "executeFunction": executeFunction,
  "triggerFlow": triggerSubflow,
  "showNotification": showNotification,
  "transformation": transformation,
  "navigate": navigate
};

function* fromEntries(payload) {
  return {
    success: true,
    output: Object.fromEntries(payload.entries)
  };
}
const transforms = {
  "fromEntries": fromEntries
};

var __defProp$4 = Object.defineProperty;
var __getOwnPropSymbols$4 = Object.getOwnPropertySymbols;
var __hasOwnProp$4 = Object.prototype.hasOwnProperty;
var __propIsEnum$4 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$4 = (obj, key, value) => key in obj ? __defProp$4(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$4 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$4.call(b, prop))
      __defNormalProp$4(a, prop, b[prop]);
  if (__getOwnPropSymbols$4)
    for (var prop of __getOwnPropSymbols$4(b)) {
      if (__propIsEnum$4.call(b, prop))
        __defNormalProp$4(a, prop, b[prop]);
    }
  return a;
};
const executors = __spreadValues$4(__spreadValues$4({}, taskExecutors), transforms);

function filter(object, path) {
  if (path === void 0) {
    return object;
  }
  if (path === null) {
    return object;
  }
  if (!isPlainObject(object)) {
    return object;
  }
  return jp.value(object, path);
}
function getResource(uri) {
  const schemeParts = uri.split(":");
  if (schemeParts[0] !== "c2" && schemeParts[0] === "be") {
    return ResourceTypes.executeBackendFlow;
  } else if (schemeParts[1] === "be") {
    return ResourceTypes.executeBackendFlow;
  }
  return schemeParts[2];
}
function merge(input, result, resultPath) {
  if (resultPath === void 0 || resultPath === null) {
    return input;
  }
  if (resultPath === "$") {
    return result;
  }
  const parsed = jp.parse(resultPath);
  parsed.reduce((target, path, index, paths) => {
    const { expression: { type, value }, operation, scope } = path;
    if (type == "root") {
      return target;
    }
    if (type === "identifier" && operation === "member" && scope === "child") {
      if (index === paths.length - 1) {
        return target[value] = result;
      }
      let child = target[value];
      if (child !== void 0 && (child === null || typeof child !== "object" || Array.isArray(child))) {
        const error2 = new Error(`Unable to match ResultPath "${resultPath}".`);
        throw error2;
      }
      if (child === void 0) {
        child = target[value] = {};
      }
      return target = child;
    }
    const error = new Error(`Invalid ResultPath "${resultPath}". ResultPath must be a Reference Path (https://states-language.net/spec.html#path).`);
    throw error;
  }, input);
  return input;
}

var __defProp$3 = Object.defineProperty;
var __defProps$3 = Object.defineProperties;
var __getOwnPropDescs$3 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$3 = Object.getOwnPropertySymbols;
var __hasOwnProp$3 = Object.prototype.hasOwnProperty;
var __propIsEnum$3 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$3 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$3.call(b, prop))
      __defNormalProp$3(a, prop, b[prop]);
  if (__getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(b)) {
      if (__propIsEnum$3.call(b, prop))
        __defNormalProp$3(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$3 = (a, b) => __defProps$3(a, __getOwnPropDescs$3(b));
function task(spec, subFlowTrigger) {
  const run = function* (input, context) {
    var _a;
    const actionType = getResource(spec.Resource);
    const filteredInput = filter(input, spec.InputPath);
    let effectiveInput = filteredInput;
    if (spec.Parameters) {
      try {
        effectiveInput = processPayloadTemplate(
          spec.Parameters,
          effectiveInput
        );
      } catch (err) {
        return {
          success: false,
          errorMsg: err.message
        };
      }
    }
    let result;
    if (actionType === "transform") {
      result = executeTransformTask(
        effectiveInput,
        effectiveInput
      );
    } else {
      const executor = executors[actionType];
      result = yield call(executor, effectiveInput, subFlowTrigger);
    }
    if (!result) {
      return {
        success: false,
        errorMsg: "failed to execute task"
      };
    }
    if (result.success) {
      let output = (_a = result.output) != null ? _a : {};
      if (spec.ResultSelector) {
        try {
          output = processPayloadTemplate(
            spec.ResultSelector,
            __spreadProps$3(__spreadValues$3({}, input), {
              result: output
            })
          );
        } catch (err) {
          return {
            success: false,
            errorMsg: err.message
          };
        }
      }
      const mergedResult = spec.ResultPath === "$$.vars" ? __spreadProps$3(__spreadValues$3({}, input), {
        vars: __spreadValues$3(__spreadValues$3({}, input.vars), output)
      }) : merge(input, output, spec.ResultPath);
      const effectiveResult = filter(mergedResult, spec.OutputPath);
      result.output = effectiveResult;
    }
    return __spreadProps$3(__spreadValues$3({}, result), {
      next: spec.Next
    });
  };
  return {
    run
  };
}
function executeTransformTask(payload, jexlContext) {
  const { template, extend, input, variableName } = payload;
  if (Array.isArray(input)) {
    const transformResult = input.map((item) => {
      const context = __spreadProps$3(__spreadValues$3({}, jexlContext), {
        [variableName]: item
      });
      try {
        const jexlResult = processPayloadTemplate(
          template,
          context
        );
        if (extend) {
          return __spreadValues$3(__spreadValues$3({}, item), jexlResult);
        } else {
          return jexlResult;
        }
      } catch (err) {
        return item;
      }
    });
    return {
      success: true,
      output: transformResult
    };
  } else {
    try {
      const context = __spreadProps$3(__spreadValues$3({}, jexlContext), {
        [variableName]: input
      });
      const jexlResult = processPayloadTemplate(
        template,
        context
      );
      if (extend) {
        return {
          success: true,
          output: __spreadValues$3(__spreadValues$3({}, input), jexlResult)
        };
      } else {
        return {
          success: true,
          output: jexlResult
        };
      }
    } catch (err) {
      return {
        success: false,
        errorMsg: err.message
      };
    }
  }
}

var __defProp$2 = Object.defineProperty;
var __defProps$2 = Object.defineProperties;
var __getOwnPropDescs$2 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$2 = (a, b) => __defProps$2(a, __getOwnPropDescs$2(b));
function pass(spec) {
  const run = function* (input, context) {
    var _a;
    const isBreakStep = spec.OutputPath === "$$.break";
    const filteredInput = filter(input, spec.InputPath);
    let effectiveInput = filteredInput;
    if (spec.Parameters) {
      try {
        effectiveInput = processPayloadTemplate(spec.Parameters, effectiveInput);
      } catch (err) {
        return {
          success: false,
          errorMsg: err.message
        };
      }
    }
    const output = isBreakStep ? { break: true } : (_a = spec.Result) != null ? _a : effectiveInput;
    const mergedResult = spec.ResultPath === "$$.vars" ? __spreadProps$2(__spreadValues$2({}, input), {
      vars: __spreadValues$2(__spreadValues$2({}, input.vars), output)
    }) : merge(input, output, spec.ResultPath);
    let effectiveResult = mergedResult;
    if (!isBreakStep) {
      effectiveResult = filter(effectiveResult, spec.OutputPath);
    }
    return {
      success: true,
      output: effectiveResult,
      next: spec.Next
    };
  };
  return {
    run
  };
}

function fail(spec) {
  const run = function* (input, context) {
    return {
      success: false,
      errorMsg: spec.Cause
    };
  };
  return {
    run
  };
}

function succeed(spec) {
  const run = function* (input, context) {
    return {
      success: true,
      output: {}
    };
  };
  return {
    run
  };
}

const aslStateFactory = (spec, subFlowTrigger) => {
  switch (spec.Type) {
    case "Task":
      return task(spec, subFlowTrigger);
    case "Choice":
      return choice(spec);
    case "Map":
      return map(spec, subFlowTrigger);
    case "Pass":
      return pass(spec);
    case "Succeed":
      return succeed();
    case "Fail":
      return fail(spec);
  }
};

function isBackendStep(state, inBackendChain) {
  if (state.Type === "Task") {
    if (getResource(state.Resource) === ResourceTypes.executeBackendFlow) {
      return true;
    }
  } else if (inBackendChain) {
    return true;
  }
  return false;
}

var __defProp$1 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
function* flowInitiator(action) {
  const { flow, executionId, parentId } = action.payload;
  const input = getInputObject(executionId);
  const context = getContext(executionId);
  const flowId = typeof flow === "string" ? flow : parentId != null ? parentId : "";
  const startAt = typeof flow === "string" ? getManifest(flow).StartAt : flow.StartAt;
  if (flowId === "") {
    throw new Error("No flow id or parent id specified");
  }
  const startState = getState(flowId, startAt);
  const identifier = {
    executionId,
    flowId,
    stateId: startAt
  };
  yield fork(run, startState, input, context, identifier);
  const chan = getChannel(executionId);
  const flowResult = yield take(chan);
  const inputObject = getInputObject(executionId);
  const contextObject = getContext(executionId);
  finalize(executionId);
  getFlowMetadata().mode === "debug" && typeof flow === "string" && console.timeEnd(`${executionId}`);
  if (flowResult.success) {
    return {
      success: true,
      output: flowResult.output,
      inputObject,
      contextObject
    };
  }
  return {
    success: false,
    errorMsg: flowResult.errorMsg,
    inputObject,
    contextObject
  };
}
function* run(state, input, context, identifier) {
  var _a, _b, _c, _d;
  if (isBackendStep(state)) {
    let effectiveInput = input;
    if (state.Type === "Task") {
      const filteredInput = filter(input, state.InputPath);
      let processedInput = filteredInput;
      if (state.Parameters) {
        try {
          processedInput = processPayloadTemplate(
            state.Parameters,
            filteredInput
          );
        } catch (err) {
          console.error(err);
        }
      }
      if (processedInput.input) {
        effectiveInput = processedInput.input;
      } else {
        effectiveInput = __spreadValues$1(__spreadValues$1({}, input), processedInput);
      }
    }
    (_d = (_c = getFlowMetadata()).onBackendTask) == null ? void 0 : _d.call(
      _c,
      {
        executionId: identifier.executionId,
        feFlowId: identifier.flowId,
        beFlowId: (_b = (_a = state.Parameters) == null ? void 0 : _a.flowId) != null ? _b : "",
        stepId: identifier.stateId
      },
      effectiveInput,
      {}
    );
    yield fork(
      onActionCompleted,
      {
        suspend: true,
        success: true,
        output: {}
        // ignored
      },
      identifier
    );
    return;
  }
  const runnable = aslStateFactory(state, triggerFlow);
  const appVars = getFlowMetadata().getAppVariables();
  const pageVars = getFlowMetadata().getPageVariables();
  const stateInput = __spreadProps$1(__spreadValues$1({}, input), {
    app: appVars,
    page: pageVars
  });
  const stateResult = yield call(runnable.run, stateInput, context, identifier);
  if (stateResult.success) {
    setInputObject(
      identifier.executionId,
      stateResult.output
    );
  }
  yield fork(onActionCompleted, stateResult, identifier);
}
function* onActionCompleted(result, identifier) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  if (result.suspend) {
    return;
  }
  const { success, next } = result;
  const { executionId, flowId } = identifier;
  const inputObject = getInputObject(executionId);
  const contextObject = getContext(executionId);
  if (!success) {
    const { errorMsg } = result;
    (_a = getChannel(executionId)) == null ? void 0 : _a.put({
      success: false,
      errorMsg
    });
    getFlowMetadata().mode === "debug" && ((_c = (_b = getFlowMetadata()).onStepCompleted) == null ? void 0 : _c.call(_b, {
      success: false,
      errorMsg,
      input: inputObject,
      context: contextObject,
      identifier
    }));
    return;
  }
  if (!next) {
    const { output } = result;
    (_d = getChannel(executionId)) == null ? void 0 : _d.put({
      success: true,
      output
    });
    getFlowMetadata().mode === "debug" && ((_f = (_e = getFlowMetadata()).onStepCompleted) == null ? void 0 : _f.call(_e, {
      success: true,
      output,
      input: inputObject,
      context: contextObject,
      identifier
    }));
    return;
  }
  const nextState = getState(flowId, next);
  if (!nextState) {
    const { output } = result;
    (_g = getChannel(executionId)) == null ? void 0 : _g.put({
      success: true,
      output
    });
    getFlowMetadata().mode === "debug" && ((_i = (_h = getFlowMetadata()).onStepCompleted) == null ? void 0 : _i.call(_h, {
      success: true,
      output,
      input: inputObject,
      context: contextObject,
      identifier
    }));
    return;
  }
  const nextStateIdentifier = {
    executionId,
    flowId,
    stateId: next
  };
  yield fork(run, nextState, inputObject, contextObject, nextStateIdentifier);
}
function* triggerFlow(flow, input, context, parentId) {
  const executionId = flowInitializer(input, context);
  return yield call(flowInitiator, {
    type: FlowControlActionType.triggerFlow,
    payload: { flow, executionId, parentId }
  });
}
function* triggerAtState(action) {
  yield fork(
    triggerExecutionAtState,
    action.payload.executionId,
    action.payload.flowId,
    action.payload.stateId,
    action.payload.previousStateId,
    action.payload.input,
    action.payload.context
  );
}
function* triggerExecutionAtState(executionId, flowId, stateId, previousStateId, input, context) {
  const identifier = {
    executionId,
    flowId,
    stateId
  };
  if (input.status && input.status === "failed") {
    const fail = input;
    yield fork(showNotification, {
      detail: fail.cause,
      severity: "error",
      summary: fail.error
    });
    return;
  }
  const taskOutput = input.output ? input.output : input;
  const state = getState(flowId, stateId);
  const prevState = getState(flowId, previousStateId);
  const inputObject = getInputObject(executionId);
  let output = {};
  const spec = prevState;
  if (spec.ResultSelector) {
    try {
      output = processPayloadTemplate(spec.ResultSelector, __spreadProps$1(__spreadValues$1({}, inputObject), {
        result: taskOutput
      }));
    } catch (err) {
      console.error(err);
    }
  }
  const mergedResult = spec.ResultPath === "$$.vars" ? __spreadProps$1(__spreadValues$1({}, inputObject), {
    vars: __spreadValues$1(__spreadValues$1({}, input.vars), output)
  }) : merge(input, output, spec.ResultPath);
  const effectiveResult = filter(
    mergedResult,
    spec.OutputPath
  );
  setInputObject(executionId, effectiveResult);
  setContext(executionId, context);
  yield fork(run, state, effectiveResult, context, identifier);
}
function* onExecutionError(action) {
  var _a, _b, _c;
  const { errorMsg, fail } = action.payload;
  if (errorMsg) {
    console.error(`Flow execution error: ${action.payload.errorMsg}`);
  }
  if (fail) {
    yield fork(showNotification, {
      detail: fail.cause,
      severity: "error",
      summary: fail.error
    });
  }
  (_c = getChannel(action.payload.executionId)) == null ? void 0 : _c.put({
    success: false,
    errorMsg: (_b = (_a = action.payload.errorMsg) != null ? _a : fail == null ? void 0 : fail.error) != null ? _b : "Something went wrong"
  });
}
function* flowActionWatcherSaga() {
  yield takeEvery(
    FlowControlActionType.triggerFlow,
    function* spawnTrigger(action) {
      yield spawn(flowInitiator, action);
    }
  );
  yield takeEvery(
    FlowControlActionType.triggerAtState,
    function* spawnTrigger(action) {
      yield spawn(triggerAtState, action);
    }
  );
  yield takeEvery(
    FlowControlActionType.executionError,
    function* spawnTrigger(action) {
      yield spawn(onExecutionError, action);
    }
  );
}

function* rootSaga() {
  yield flowActionWatcherSaga();
}

const initialState = {};
const executionSlice = createSlice({
  name: "execution",
  initialState,
  reducers: {
    dummy: (state, action) => {
      return state;
    }
  }
});
var ExecutionStateReducer = executionSlice.reducer;

const sagaMiddleware = createSagaMiddleware();
const store = configureStore({
  reducer: {
    execution: ExecutionStateReducer
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(sagaMiddleware)
});
sagaMiddleware.run(rootSaga);
const flowDispatch = store.dispatch;

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const flowModule = (settings) => {
  setFlowMetadata(settings);
  function trigger(flow, input, context) {
    const executionId = flowInitializer(input, context);
    getFlowMetadata().mode === "debug" && console.time(`${executionId}`);
    flowDispatch({
      type: FlowControlActionType.triggerFlow,
      payload: {
        flow,
        executionId
      }
    });
  }
  function triggerExecutionAtState(executionId, flowId, stateId, previousStateId, maybeInput, maybeContext) {
    const input = maybeInput != null ? maybeInput : {};
    const context = maybeContext != null ? maybeContext : {};
    flowDispatch({
      type: FlowControlActionType.triggerAtState,
      payload: {
        executionId,
        flowId,
        stateId,
        previousStateId,
        input,
        context
      }
    });
  }
  function executionError(payload) {
    flowDispatch({
      type: FlowControlActionType.executionError,
      payload
    });
  }
  function setOnStepCompleted(callback) {
    setFlowMetadata(__spreadProps(__spreadValues({}, getFlowMetadata()), {
      onStepCompleted: callback
    }));
  }
  function setOnBackendTask(callback) {
    setFlowMetadata(__spreadProps(__spreadValues({}, getFlowMetadata()), {
      onBackendTask: callback
    }));
  }
  function setOnNavigateCallback(callback) {
    setFlowMetadata(__spreadProps(__spreadValues({}, getFlowMetadata()), {
      onNavigate: callback
    }));
  }
  function setFlowManifest(manifest) {
    setFlowMetadata(__spreadProps(__spreadValues({}, getFlowMetadata()), {
      flowManifest: manifest
    }));
  }
  return {
    trigger,
    triggerExecutionAtState,
    setOnStepCompleted,
    setOnBackendTask,
    setOnNavigateCallback,
    executionError,
    setFlowManifest
  };
};

function hasNext(state) {
  const isNextAvailableType = state.Type === "Task" || state.Type === "Map";
  return isNextAvailableType && typeof state.Next !== "undefined";
}
function hasEnd(state) {
  const isEndAvailableType = state.Type === "Task" || state.Type === "Map" || state.Type === "Pass";
  return isEndAvailableType && typeof state.End !== void 0;
}
function isTaskState(state) {
  return state.Type === "Task";
}
function isChoiceState(state) {
  return state.Type === "Choice";
}
function isMapState(state) {
  return state.Type === "Map";
}
function isPassState(state) {
  return state.Type === "Pass";
}

export { flowModule as FlowModule, hasEnd, hasNext, isChoiceState, isMapState, isPassState, isTaskState };
//# sourceMappingURL=index.mjs.map
